import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTabsModule } from '@angular/material/tabs';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FormlySelectOption, FormlySelectOptionsPipe } from '@ngx-formly/core/select';

import { ContentContainerComponent } from '@shared/preloader';
import { GenericButtonComponent } from '@shared/ui';
import { DateStore } from './date.store';
import { DEFAULT_VISIBLE_OPTIONS_COUNT } from './date.util';
import { CalendarDialogComponent } from './calendar/calendar.component';

@Component({
  selector: 'fc-date',
  templateUrl: './date.component.html',
  styleUrl: './date.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DateStore, FormlySelectOptionsPipe],
  imports: [
    A11yModule,
    ContentContainerComponent,
    FormsModule,
    GenericButtonComponent,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatTabsModule,
    ReactiveFormsModule,
    ReactiveFormsModule,
  ],
})
export class FormlyFieldDateComponent extends FieldType<FieldTypeConfig> implements OnInit {

  private dialog = inject(MatDialog);

  protected readonly destroyRef = inject(DestroyRef);
  readonly store = inject(DateStore);
  protected readonly formlySelectOptionsPipe = inject(FormlySelectOptionsPipe);
  protected readonly DEFAULT_VISIBLE_OPTIONS_COUNT = DEFAULT_VISIBLE_OPTIONS_COUNT;
  protected readonly emptyOption: FormlySelectOption = { label: 'None', value: null };

  ngOnInit(): void {
    const f = this.field;

    if (typeof f?.props?.['visibleOptionsCount'] === 'number') {
      this.store.setInitialVisibleOptionsCount(f?.props?.['visibleOptionsCount']);
    }

    this.store.setOptions(
      this.formlySelectOptionsPipe.transform(f?.props?.options ?? [], f),
      f?.formControl?.value ?? [],
    );

    f?.formControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(values => {
      if (values) {
        this.store.toggleOption(values);
      } else {
        this.store.toggleOption(null);
      }
    });
  }

  openCalendar(opt?: FormlySelectOption) {
    this.dialog
      .open(CalendarDialogComponent, { data: opt })
      .componentInstance
      .closeForm
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(modal => {
        if (!modal.confirmed || !modal.value) {
          return modal.close();
        }

        this.setPeriodOption(modal.value);
        modal.close();
      });
  }

  private setPeriodOption(opt: FormlySelectOption): void {
    this.store.setPeriodOption(opt);
    this.field?.formControl?.setValue(opt.value)
  }
}
